import type { ReactNode } from "react";

import type { PublicService } from "../types";
import { minutesLabel, servicePriceLabel } from "../utils/labels";
import { card, figure, label } from "./section";
import { cn } from "@/lib/utils";

export type PriceLine = { key: string; name: string; detail: string; price: string | null };

/** A booking service as a price line. `describe` adds the service's own description after the length. */
export const priceLineOf = (service: PublicService, currency: string, describe = false): PriceLine => ({
  key: service.id,
  name: service.name,
  detail: [minutesLabel(service.durationMinutes), describe ? service.description : ""].filter(Boolean).join(", "),
  price: servicePriceLabel(service, currency),
});

/** A card of price lines: what it is and how long on the left, what it costs on the right. */
export const PriceLines = ({
  title,
  lines,
  action,
  className,
}: {
  /** A heading inside the card, for a list that is one group of several. */
  title?: string;
  lines: PriceLine[];
  /** Something to do with a line, such as a book button. */
  action?: (line: PriceLine) => ReactNode;
  className?: string;
}) => (
  <div className={cn(card, !title && "py-3 md:py-3.5", className)}>
    {title ? <h3 className="pb-3 text-2xl font-medium tracking-[-0.03em]">{title}</h3> : null}
    {lines.map((line) => (
      <div
        key={line.key}
        className={cn(
          "flex items-center justify-between gap-3 border-t border-[var(--site-dot)] py-3.5 lg:gap-5",
          !title && "first:border-0",
        )}
      >
        <span className="min-w-0 flex-1 font-medium">
          {line.name}
          <small className={cn(label, "block font-normal")}>{line.detail}</small>
        </span>
        {line.price ? <b className={cn(figure, "text-[22px] whitespace-nowrap")}>{line.price}</b> : null}
        {action ? action(line) : null}
      </div>
    ))}
  </div>
);
